import { useF0FormDefinition } from '@factorialco/f0-react'
import type { F0WizardFormPerSectionSubmitArg } from '@factorialco/f0-react'

import { createGeneralInfoStepSchema } from 'modules/planning/components/PeriodWizard/steps/general-info/schema'
import { createGeneralInfoWizardStep } from 'modules/planning/components/PeriodWizard/steps/general-info/step'
import { createScopeStepSchema } from 'modules/planning/components/PeriodWizard/steps/scope/schema'
import { createScopeWizardStep } from 'modules/planning/components/PeriodWizard/steps/scope/step'
import { createCollaboratorsStepSchema } from 'modules/planning/components/PeriodWizard/steps/collaborators/schema'
import { createCollaboratorsWizardStep } from 'modules/planning/components/PeriodWizard/steps/collaborators/step'
import type { WizardData } from 'modules/planning/components/PeriodWizard/types'

type SectionSchemas = {
  general: ReturnType<typeof createGeneralInfoStepSchema>
  scope: ReturnType<typeof createScopeStepSchema>
  collaborators: ReturnType<typeof createCollaboratorsStepSchema>
}

type WizardTranslations = {
  generalInfoStepTitle: () => string
  scopeStepTitle: () => string
  scopeStepDescription: () => string
  collaboratorsStepTitle: () => string
  collaboratorsStepDescription: () => string
  next: () => string
  previous: () => string
  finish: () => string
}

interface WizardDefaultValues {
  general?: {
    name?: string
    startDate?: Date
    endDate?: Date
  }
  scope?: {
    scopeEmployeeIds?: string[]
  }
  collaborators?: {
    collaborators?: Array<{ employeeId: string; role: 'editor' | 'viewer' }>
  }
}

interface UseNewPeriodWizardFormDefinitionProps {
  translations: WizardTranslations
  onFinalSubmit: (data: WizardData) => Promise<boolean>
  defaultValues?: WizardDefaultValues
  datesDisabled?: boolean
}

export const useNewPeriodWizardFormDefinition = ({
  translations,
  onFinalSubmit,
  defaultValues,
  datesDisabled = false,
}: UseNewPeriodWizardFormDefinitionProps) => {
  const formDefinition = useF0FormDefinition({
    name: 'planning-new-period-wizard',
    schema: {
      general: createGeneralInfoStepSchema({ datesDisabled }),
      scope: createScopeStepSchema(),
      collaborators: createCollaboratorsStepSchema(),
    },
    ...(defaultValues ? { defaultValues } : {}),
    sections: {
      general: {
        title: translations.generalInfoStepTitle(),
      },
      scope: {
        title: translations.scopeStepTitle(),
        description: translations.scopeStepDescription(),
      },
      collaborators: {
        title: translations.collaboratorsStepTitle(),
        description: translations.collaboratorsStepDescription(),
      },
    },
    onSubmit: async ({ sectionId, fullData }: F0WizardFormPerSectionSubmitArg<SectionSchemas>) => {
      if (sectionId !== 'collaborators') {
        return { success: true }
      }

      const submitted = await onFinalSubmit({
        ...fullData.general,
        ...fullData.scope,
        collaborators: fullData.collaborators.collaborators,
      })

      return { success: submitted }
    },
  })

  const steps = [
    createGeneralInfoWizardStep(translations),
    createScopeWizardStep(translations),
    createCollaboratorsWizardStep(translations),
  ]

  return { formDefinition, steps }
}
